import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * Avatar humanoide 3D real usando three.js (sin drei por incompatibilidad node20).
 * Renderiza una figura volumétrica con:
 *   - Cabeza (esfera)
 *   - Torso (caja con textura de jersey)
 *   - Brazos + piernas (cilindros)
 *   - Pala en mano (caja larga)
 *   - Plataforma reflejo
 * Soporta rotación con drag del mouse o touch.
 */
export default function ThreeAvatar({
  brandColors = {}, outfit = {}, height = 500,
  clubLogoUrl = null, palaLogoUrl = null,
  chestLogoUrl = null, shortLogoUrl = null, shoesLogoUrl = null, capLogoUrl = null,
  animation = "idle", cameraMode = "default", facePhotoUrl = null,
}) {
  const mountRef = useRef(null);
  const sceneRefs = useRef({ rightArm: null, palaGroup: null, camera: null, lookTarget: new THREE.Vector3(0, 1.4, 0) });

  const {
    jersey = "#CCFF00",
    pants = "#0A0A0A",
    shoes = "#E63946",
    pala = "#00A0FF",
    skin = "#E8C39E",
  } = brandColors;

  // Outfit options
  const apparelType = outfit.apparel || "short"; // short | long | tank
  const shoesType = outfit.shoes || "low";       // low | high
  const headwear = outfit.headwear || "none";    // none | cap | band

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const width = mount.clientWidth;
    const h = height;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0a);
    scene.fog = new THREE.Fog(0x0a0a0a, 6, 20);

    // Camera — "face" mode zooms in on the head using the selfie texture.
    const camera = new THREE.PerspectiveCamera(40, width / h, 0.1, 100);
    if (cameraMode === "face") {
      camera.position.set(0, 1.65, 1.1);
      camera.lookAt(0, 1.65, 0);
    } else {
      camera.position.set(0, 1.6, 5.5);
      camera.lookAt(0, 1.4, 0);
    }

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, h);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mount.appendChild(renderer.domElement);

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.55);
    scene.add(ambient);
    const key = new THREE.DirectionalLight(0xccff00, 0.95);
    key.position.set(3, 6, 4);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x00a0ff, 0.6);
    rim.position.set(-3, 4, -3);
    scene.add(rim);
    const fill = new THREE.DirectionalLight(0xff9500, 0.35);
    fill.position.set(0, 3, 5);
    scene.add(fill);

    // Floor (mirror-ish disc)
    const floorGeo = new THREE.CircleGeometry(4, 64);
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.4, metalness: 0.55 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);

    // Floor ring (lime accent)
    const ringGeo = new THREE.RingGeometry(3.7, 4, 64);
    const ringMat = new THREE.MeshBasicMaterial({ color: 0xccff00, side: THREE.DoubleSide, transparent: true, opacity: 0.6 });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.01;
    scene.add(ring);

    // Avatar group
    const avatar = new THREE.Group();

    // ============================================================
    // Logo decal helper — renders a crisp, high-legibility logo plane
    // positioned in front of a target mesh. Uses transparent PNG w/ alpha.
    // Uses polygonOffset so the decal is drawn on top of its parent surface
    // (no z-fighting), and unlit MeshBasicMaterial so colors read correctly
    // regardless of scene lighting. An optional white backing plane behind
    // the logo makes dark logos legible on dark shirts.
    // ============================================================
    const maxAniso = renderer.capabilities.getMaxAnisotropy();
    const makeLogoDecal = (url, w, h, opts = {}) => {
      if (!url) return null;
      const { backing = true, backingOpacity = 0.9 } = opts;
      const group = new THREE.Group();
      if (backing) {
        const bg = new THREE.Mesh(
          new THREE.PlaneGeometry(w * 1.1, h * 1.1),
          new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: backingOpacity,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -2,
            polygonOffsetUnits: -2,
            side: THREE.DoubleSide,
          })
        );
        bg.renderOrder = 5;
        group.add(bg);
      }
      const mat = new THREE.MeshBasicMaterial({
        transparent: true,
        alphaTest: 0.05,
        side: THREE.DoubleSide,
        depthWrite: false,
        opacity: 0,
        polygonOffset: true,
        polygonOffsetFactor: -4,
        polygonOffsetUnits: -4,
      });
      const loader = new THREE.TextureLoader();
      loader.setCrossOrigin("anonymous");
      loader.load(
        url,
        (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          tex.anisotropy = maxAniso;
          tex.magFilter = THREE.LinearFilter;
          tex.minFilter = THREE.LinearMipmapLinearFilter;
          tex.generateMipmaps = true;
          mat.map = tex;
          mat.alphaMap = tex;
          mat.opacity = 1;
          mat.needsUpdate = true;
        },
        undefined,
        () => { /* silent fail */ }
      );
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
      mesh.renderOrder = 10;
      group.add(mesh);
      return group;
    };

    // ============================================================
    // Body materials
    const skinMat = new THREE.MeshStandardMaterial({ color: skin, roughness: 0.7, metalness: 0.05 });
    const jerseyMat = new THREE.MeshStandardMaterial({ color: jersey, roughness: 0.55, metalness: 0.1 });
    const pantsMat = new THREE.MeshStandardMaterial({ color: pants, roughness: 0.65 });
    const shoesMat = new THREE.MeshStandardMaterial({ color: shoes, roughness: 0.4, metalness: 0.25 });
    const palaMat = new THREE.MeshStandardMaterial({ color: pala, roughness: 0.3, metalness: 0.6, emissive: pala, emissiveIntensity: 0.18 });

    // Helper
    const addPart = (geo, mat, x, y, z, rotX = 0, rotZ = 0) => {
      const m = new THREE.Mesh(geo, mat);
      m.position.set(x, y, z);
      m.rotation.x = rotX;
      m.rotation.z = rotZ;
      m.castShadow = true;
      avatar.add(m);
      return m;
    };

    // Head — with optional user-selfie texture (volumetric via displacementMap)
    let headMaterial = skinMat;
    if (facePhotoUrl) {
      const loader = new THREE.TextureLoader();
      const tex = loader.load(facePhotoUrl);
      tex.colorSpace = THREE.SRGBColorSpace;
      // Center face on front-facing hemisphere (offset 0.25, repeat 0.5)
      tex.offset.set(0.25, 0);
      tex.repeat.set(0.5, 1);
      headMaterial = new THREE.MeshStandardMaterial({
        map: tex,
        displacementMap: tex,
        displacementScale: 0.04,
        roughness: 0.55,
        metalness: 0.05,
      });
    }
    const headMesh = new THREE.Mesh(new THREE.SphereGeometry(0.22, 64, 64), headMaterial);
    headMesh.position.set(0, 2.55, 0);
    headMesh.castShadow = true;
    avatar.add(headMesh);
    // Hair / Headwear
    const hairMat = new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.9 });
    let capMesh = null; // reference for cap logo placement
    if (headwear === "cap") {
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.245, 24, 24, 0, Math.PI * 2, 0, Math.PI / 2), new THREE.MeshStandardMaterial({ color: jersey, roughness: 0.7 }));
      cap.position.set(0, 2.6, 0);
      cap.castShadow = true;
      avatar.add(cap);
      capMesh = cap;
      const visor = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.04, 0.18), new THREE.MeshStandardMaterial({ color: jersey, roughness: 0.7 }));
      visor.position.set(0, 2.55, 0.2);
      visor.castShadow = true;
      avatar.add(visor);
      // Cap logo decal on the front-top of the cap (over the visor)
      const capLogo = makeLogoDecal(capLogoUrl || clubLogoUrl, 0.32, 0.16);
      if (capLogo) {
        capLogo.position.set(0, 2.72, 0.255);
        avatar.add(capLogo);
      }
    } else if (headwear === "band") {
      const band = new THREE.Mesh(new THREE.TorusGeometry(0.21, 0.025, 12, 32), new THREE.MeshStandardMaterial({ color: jersey, roughness: 0.6 }));
      band.position.set(0, 2.65, 0);
      band.rotation.x = Math.PI / 2;
      avatar.add(band);
      const hair = new THREE.Mesh(new THREE.SphereGeometry(0.235, 32, 32, 0, Math.PI * 2, 0, Math.PI / 2), hairMat);
      hair.position.set(0, 2.6, 0);
      hair.castShadow = true;
      avatar.add(hair);
    } else {
      const hair = new THREE.Mesh(new THREE.SphereGeometry(0.235, 32, 32, 0, Math.PI * 2, 0, Math.PI / 2), hairMat);
      hair.position.set(0, 2.6, 0);
      hair.castShadow = true;
      avatar.add(hair);
    }
    // Neck
    addPart(new THREE.CylinderGeometry(0.07, 0.08, 0.1, 16), skinMat, 0, 2.32, 0);

    // Torso (jersey) — solid material; logo goes as decal for legibility
    addPart(new THREE.BoxGeometry(0.55, 0.7, 0.32), jerseyMat, 0, 1.9, 0);
    // Chest+belly logo decal — covers full front of the torso (pecho y panza)
    const chestLogo = makeLogoDecal(chestLogoUrl || clubLogoUrl, 0.5, 0.6);
    if (chestLogo) {
      chestLogo.position.set(0, 1.9, 0.175);
      avatar.add(chestLogo);
    }
    // Hips
    addPart(new THREE.BoxGeometry(0.5, 0.18, 0.3), jerseyMat, 0, 1.5, 0);

    // Arms — different geometry per apparelType
    const sleeveTop = apparelType === "tank" ? 0.15 : apparelType === "long" ? 0.5 : 0.32;
    const upperArmIsSkin = apparelType === "tank";
    const lowerArmIsSkin = apparelType !== "long";
    // Left upper arm (static)
    addPart(new THREE.CylinderGeometry(0.07, 0.06, sleeveTop, 16), upperArmIsSkin ? skinMat : jerseyMat, -0.36, 2.05 - sleeveTop / 2, 0, 0, Math.PI / 18);
    if (upperArmIsSkin) {
      addPart(new THREE.CylinderGeometry(0.06, 0.055, 0.4, 16), skinMat, -0.4, 1.7, 0, 0, Math.PI / 22);
    }
    addPart(new THREE.CylinderGeometry(0.06, 0.055, 0.45, 16), lowerArmIsSkin ? skinMat : jerseyMat, -0.43, 1.55, 0, 0, Math.PI / 22);
    addPart(new THREE.SphereGeometry(0.07, 16, 16), skinMat, -0.5, 1.3, 0);

    // RIGHT arm as articulated group (shoulder pivot) for swing animation
    const rightArm = new THREE.Group();
    rightArm.position.set(0.34, 2.15, 0);
    const upperR = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.06, sleeveTop, 16), upperArmIsSkin ? skinMat : jerseyMat);
    upperR.position.set(0.02, -sleeveTop / 2, 0);
    upperR.rotation.z = -Math.PI / 18;
    upperR.castShadow = true;
    rightArm.add(upperR);
    if (upperArmIsSkin) {
      const skinUR = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.055, 0.4, 16), skinMat);
      skinUR.position.set(0.06, -0.45, 0);
      skinUR.rotation.z = -Math.PI / 22;
      skinUR.castShadow = true;
      rightArm.add(skinUR);
    }
    const forearmR = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.055, 0.55, 16), lowerArmIsSkin ? skinMat : jerseyMat);
    forearmR.position.set(0.21, -0.6, 0.15);
    forearmR.rotation.x = Math.PI / 6;
    forearmR.rotation.z = -Math.PI / 10;
    forearmR.castShadow = true;
    rightArm.add(forearmR);
    const handR = new THREE.Mesh(new THREE.SphereGeometry(0.07, 16, 16), skinMat);
    handR.position.set(0.34, -0.79, 0.32);
    handR.castShadow = true;
    rightArm.add(handR);
    avatar.add(rightArm);

    sceneRefs.current.rightArm = rightArm;

    // Pala (paddle racket) — solid head; logo as decal on front face for legibility
    const palaGroup = new THREE.Group();
    const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.25, 16), new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.8 }));
    handle.position.y = 0.13;
    handle.castShadow = true;
    palaGroup.add(handle);
    const head = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.38, 0.04), palaMat);
    head.position.y = 0.4;
    head.castShadow = true;
    palaGroup.add(head);
    const headEdge = new THREE.Mesh(new THREE.TorusGeometry(0.18, 0.018, 12, 32), new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.6, metalness: 0.4 }));
    headEdge.position.y = 0.4;
    headEdge.rotation.y = Math.PI / 2;
    palaGroup.add(headEdge);
    // Pala logo decal — cubre casi todo el frente de la cara de la pala
    const palaDecal = makeLogoDecal(palaLogoUrl, 0.3, 0.36);
    if (palaDecal) {
      palaDecal.position.set(0, 0.4, 0.035);
      palaGroup.add(palaDecal);
      // Also on back face for visibility during swing
      const palaBack = makeLogoDecal(palaLogoUrl, 0.3, 0.36);
      if (palaBack) {
        palaBack.position.set(0, 0.4, -0.035);
        palaBack.rotation.y = Math.PI;
        palaGroup.add(palaBack);
      }
    }
    palaGroup.position.set(0.4, -0.75, 0.32);
    palaGroup.rotation.z = -Math.PI / 5;
    palaGroup.rotation.x = Math.PI / 8;
    rightArm.add(palaGroup);
    sceneRefs.current.palaGroup = palaGroup;

    // Legs
    addPart(new THREE.CylinderGeometry(0.1, 0.09, 0.65, 16), pantsMat, -0.14, 1.05, 0);
    addPart(new THREE.CylinderGeometry(0.1, 0.09, 0.65, 16), pantsMat, 0.14, 1.05, 0);
    addPart(new THREE.CylinderGeometry(0.09, 0.08, 0.55, 16), pantsMat, -0.14, 0.45, 0);
    addPart(new THREE.CylinderGeometry(0.09, 0.08, 0.55, 16), pantsMat, 0.14, 0.45, 0);
    // Knees (joint hint)
    addPart(new THREE.SphereGeometry(0.1, 16, 16), pantsMat, -0.14, 0.75, 0);
    addPart(new THREE.SphereGeometry(0.1, 16, 16), pantsMat, 0.14, 0.75, 0);
    // Short/thigh logo decal — solo muslo derecho, grande
    if (shortLogoUrl) {
      const shortR = makeLogoDecal(shortLogoUrl, 0.22, 0.22);
      if (shortR) { shortR.position.set(0.14, 1.2, 0.115); avatar.add(shortR); }
    }

    // Shoes — high or low
    const shoeH = shoesType === "high" ? 0.22 : 0.13;
    const shoeY = shoesType === "high" ? 0.17 : 0.115;
    if (shoesType === "high") {
      // Add ankle collar
      addPart(new THREE.CylinderGeometry(0.1, 0.1, 0.12, 16), shoesMat, -0.14, 0.27, 0);
      addPart(new THREE.CylinderGeometry(0.1, 0.1, 0.12, 16), shoesMat, 0.14, 0.27, 0);
    }
    addPart(new THREE.BoxGeometry(0.22, shoeH, 0.36), shoesMat, -0.14, shoeY, 0.06);
    addPart(new THREE.BoxGeometry(0.22, shoeH, 0.36), shoesMat, 0.14, shoeY, 0.06);
    // Shoe logo decals — outer side of each shoe, bigger and pushed out
    if (shoesLogoUrl) {
      const decalW = 0.18, decalH = Math.max(shoeH * 0.85, 0.12);
      const shoeLL = makeLogoDecal(shoesLogoUrl, decalW, decalH);
      if (shoeLL) { shoeLL.position.set(-0.263, shoeY, 0.06); shoeLL.rotation.y = -Math.PI / 2; avatar.add(shoeLL); }
      const shoeRR = makeLogoDecal(shoesLogoUrl, decalW, decalH);
      if (shoeRR) { shoeRR.position.set(0.263, shoeY, 0.06); shoeRR.rotation.y = Math.PI / 2; avatar.add(shoeRR); }
    }

    // Brand glow under avatar
    const glow = new THREE.PointLight(jersey, 1.2, 4, 2);
    glow.position.set(0, 0.4, 0);
    scene.add(glow);

    scene.add(avatar);

    // Drag-to-rotate
    let isDragging = false;
    let prevX = 0;
    let targetRotY = 0;
    const onPointerDown = (e) => {
      isDragging = true;
      prevX = e.clientX || (e.touches && e.touches[0].clientX) || 0;
    };
    const onPointerMove = (e) => {
      if (!isDragging) return;
      const x = e.clientX || (e.touches && e.touches[0].clientX) || prevX;
      const delta = (x - prevX) * 0.01;
      targetRotY += delta;
      prevX = x;
    };
    const onPointerUp = () => { isDragging = false; };
    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    renderer.domElement.addEventListener("touchstart", onPointerDown, { passive: true });
    window.addEventListener("touchmove", onPointerMove, { passive: true });
    window.addEventListener("touchend", onPointerUp);

    sceneRefs.current.camera = camera;
    // Save initial camera state for cinematic loops
    const camHome = { pos: camera.position.clone(), look: new THREE.Vector3(0, 1.4, 0) };

    // Animate
    const clock = new THREE.Clock();
    let rafId = 0;
    let autoRot = true;
    renderer.domElement.addEventListener("pointerdown", () => { autoRot = false; });
    const tick = () => {
      const t = clock.getElapsedTime();
      // Gentle idle: vertical bob
      avatar.position.y = Math.sin(t * 1.5) * 0.03;
      // Auto-rotate when not dragging (default + face modes)
      if (autoRot && !isDragging && (cameraMode === "default" || cameraMode === "face")) {
        targetRotY += cameraMode === "face" ? 0.006 : 0.003;
      }
      avatar.rotation.y += (targetRotY - avatar.rotation.y) * 0.1;
      // Lights pulse
      glow.intensity = 1.0 + Math.sin(t * 2) * 0.35;
      ring.material.opacity = 0.45 + Math.sin(t * 2.5) * 0.18;

      // SWING animation — only when animation === 'swing'
      if (rightArm && animation === "swing") {
        // Tennis-like forehand swing 2.5s loop
        const phase = (t % 2.5) / 2.5;
        let armX, armZ;
        if (phase < 0.4) {
          // back-swing wind-up
          const k = phase / 0.4;
          armX = THREE.MathUtils.lerp(0, -1.4, k);
          armZ = THREE.MathUtils.lerp(0, 0.5, k);
        } else if (phase < 0.65) {
          // forward swing
          const k = (phase - 0.4) / 0.25;
          armX = THREE.MathUtils.lerp(-1.4, 1.2, k);
          armZ = THREE.MathUtils.lerp(0.5, -0.6, k);
        } else {
          // follow-through and reset
          const k = (phase - 0.65) / 0.35;
          armX = THREE.MathUtils.lerp(1.2, 0, k);
          armZ = THREE.MathUtils.lerp(-0.6, 0, k);
        }
        rightArm.rotation.x = armX;
        rightArm.rotation.z = armZ;
      } else if (rightArm) {
        // Idle: subtle swing offset
        rightArm.rotation.x = Math.sin(t * 1.2) * 0.05;
        rightArm.rotation.z = Math.cos(t * 0.9) * 0.04;
      }

      // CINEMATIC CAMERA MODE — 360 orbit with zoom in/out cycles
      if (cameraMode === "cinema") {
        const orbitT = t * 0.4; // slower
        const radius = 5.0 + Math.sin(t * 0.6) * 1.5;
        const yOff = 1.6 + Math.sin(t * 0.5) * 0.6;
        camera.position.x = Math.sin(orbitT) * radius;
        camera.position.z = Math.cos(orbitT) * radius;
        camera.position.y = yOff;
        // Look at face during top of cycle, pala during bottom
        const lookSwitch = (Math.sin(t * 0.4) + 1) / 2; // 0..1
        const ty = THREE.MathUtils.lerp(1.4, 2.55, lookSwitch); // pala pos vs head
        camera.lookAt(0, ty, 0);
      } else if (cameraMode === "face") {
        // Keep the camera locked close to the head so the selfie texture is visible.
        camera.position.set(0, 1.65, 1.1);
        camera.lookAt(0, 1.65, 0);
      } else {
        // Smooth return to default home
        camera.position.lerp(camHome.pos, 0.05);
        camera.lookAt(camHome.look);
      }

      renderer.render(scene, camera);
      rafId = requestAnimationFrame(tick);
    };
    tick();

    // Resize
    const onResize = () => {
      const w = mount.clientWidth;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      renderer.domElement.removeEventListener("touchstart", onPointerDown);
      window.removeEventListener("touchmove", onPointerMove);
      window.removeEventListener("touchend", onPointerUp);
      mount.removeChild(renderer.domElement);
      renderer.dispose();
      scene.traverse((o) => {
        if (o.geometry) o.geometry.dispose?.();
        if (o.material) {
          if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose?.());
          else o.material.dispose?.();
        }
      });
    };
  }, [jersey, pants, shoes, pala, skin, height, apparelType, shoesType, headwear, clubLogoUrl, palaLogoUrl, chestLogoUrl, shortLogoUrl, shoesLogoUrl, capLogoUrl, animation, cameraMode, facePhotoUrl]);

  return (
    <div className="relative w-full" data-testid="three-avatar-container">
      <div ref={mountRef} className="w-full rounded-md overflow-hidden border border-[#CCFF00]/20 bg-[#0A0A0A] cursor-grab active:cursor-grabbing" style={{ height: `${height}px` }} />
      <div className="absolute bottom-2 right-2 text-[9px] tracking-[0.2em] uppercase font-bold text-[#CCFF00]/70 pointer-events-none bg-black/50 px-2 py-1 rounded">3D · arrastra para girar</div>
    </div>
  );
}
